import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getTransactionsPendingApprovalAsync } from '../transactionMandateV2/getPendingTransactionsSlice';

interface IDeclineTransactionRequest {
  transactionId: string;
  pin: string;
  reason: string;
}

interface IDeclineTransactionResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IDeclineTransactionResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const declineTransactionAsync = createAsyncThunk(
  'transaction/decline',
  async (
    values: IDeclineTransactionRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IDeclineTransactionResponse>(
        `transaction/outlet-mandates/${values.transactionId}/reject`,
        { pin: values.pin, reason: values.reason },
        config,
      );

      dispatch(getTransactionsPendingApprovalAsync({}));

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Transaction successfully approved',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IDeclineTransactionResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const transactionMandateCreationSlice = createSlice({
  name: 'transaction/decline',
  initialState,
  reducers: {
    resetTransactionDecline(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(declineTransactionAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(declineTransactionAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IDeclineTransactionResponse;
      })
      .addCase(declineTransactionAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IDeclineTransactionResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTransactionDecline } =
  transactionMandateCreationSlice.actions;
export default transactionMandateCreationSlice.reducer;
